import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Building2, Users, CalendarDays, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function SuperDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/super/dashboard").then(({ data }) => setStats(data)); }, []);

  return (
    <div className="space-y-6" data-testid="super-dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Platform</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Overview</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={Building2} label="Total companies" value={stats?.total_companies ?? "—"} testid="super-metric-companies" />
        <Metric icon={Users} label="Total users" value={stats?.total_users ?? "—"} testid="super-metric-users" />
        <Metric icon={CalendarDays} label="Total bookings" value={stats?.total_bookings ?? "—"} testid="super-metric-bookings" />
        <Metric icon={FileText} label="Total invoices" value={stats?.total_invoices ?? "—"} testid="super-metric-invoices" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-[#18181B] border-white/5 text-zinc-200">
          <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Signups (last 6 months)</CardTitle></CardHeader>
          <CardContent className="h-64">
            {stats?.signups_by_month?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.signups_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="month" stroke="#888" fontSize={12} />
                  <YAxis stroke="#888" fontSize={12} />
                  <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272A" }} />
                  <Line type="monotone" dataKey="signups" stroke="#D85C38" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="text-zinc-500 text-sm">—</div>}
          </CardContent>
        </Card>

        <Card className="bg-[#18181B] border-white/5 text-zinc-200">
          <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Companies by type</CardTitle></CardHeader>
          <CardContent className="h-64">
            {stats?.companies_by_type?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.companies_by_type} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis type="number" stroke="#888" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#888" fontSize={11} width={120} />
                  <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272A" }} />
                  <Bar dataKey="count" fill="#D85C38" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-zinc-500 text-sm">—</div>}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#18181B] border-white/5 text-zinc-200">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Recent signups</CardTitle></CardHeader>
        <CardContent>
          {stats?.recent_companies?.length ? (
            <ul className="divide-y divide-white/5">
              {stats.recent_companies.map((c) => (
                <li key={c.id} className="py-3 flex justify-between" data-testid={`recent-company-${c.id}`}>
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.email} · {c.company_type_name || "—"}</div>
                  </div>
                  <div className="text-xs text-zinc-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</div>
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-zinc-500">No companies yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#18181B] border border-white/5 rounded-md p-5 text-zinc-200" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
        <Icon size={14} />
        {label}
      </div>
      <div className="mt-3 font-serif text-3xl tracking-tighter text-white">{value}</div>
    </div>
  );
}
